/**
 * Client for the US-266 citation asset API
 * (`/api/v1/citation-assets/metadata`, `/api/v1/citation-assets/content`).
 *
 * Retrieval is SHA/ETag-aware: every successful metadata or content fetch
 * caches the descriptor's `sha256` as the ETag for that
 * (urn, package_version, asset_id) key, and subsequent content fetches send
 * `If-None-Match` so an unchanged immutable asset is revalidated with a
 * cheap 304 instead of re-downloading bytes.
 *
 * Errors are mapped to a small, deterministic, privacy-safe set of codes.
 * The server's `ProblemDetails.detail` text is never surfaced to the UI
 * (it may contain internal detail not meant for end users); only the
 * stable `extensions.code` is read and translated into a fixed, generic
 * message per code.
 */

import { getApiHost, getAuthHeaders } from 'constants/config';
import {
  CitationAssetDescriptor,
  CitationAssetErrorCode,
  CitationAssetErrorCodes,
  CitationAssetKey,
} from 'types/citationAsset';

const CITATION_ASSETS_ENDPOINT = '/api/v1/citation-assets';

export interface CitationAssetMetadataResult {
  status: 'ok';
  descriptor: CitationAssetDescriptor;
}

export interface CitationAssetErrorResult {
  status: 'error';
  code: CitationAssetErrorCode;
  httpStatus: number;
  /** Fixed, privacy-safe message suitable for direct UI display. */
  message: string;
}

export type CitationAssetResult = CitationAssetMetadataResult | CitationAssetErrorResult;

export interface CitationAssetContentOkResult {
  status: 'ok';
  descriptor: CitationAssetDescriptor;
  bytes: ArrayBuffer;
  /** True when the server returned 304 and cached bytes were reused. */
  fromCache: boolean;
}

export type CitationAssetContentResult = CitationAssetContentOkResult | CitationAssetErrorResult;

interface CacheEntry {
  descriptor: CitationAssetDescriptor;
  etag: string;
  bytes?: ArrayBuffer;
}

const cache = new Map<string, CacheEntry>();

function cacheKey(key: CitationAssetKey): string {
  return `${key.urn}|${key.packageVersion}|${key.assetId}`;
}

/** Test-only hook to reset the in-memory ETag/content cache between tests. */
export function __resetCitationAssetCache(): void {
  cache.clear();
}

const PRIVACY_SAFE_MESSAGES: Record<CitationAssetErrorCode, string> = {
  [CitationAssetErrorCodes.UrnRequired]: 'This citation could not be resolved.',
  [CitationAssetErrorCodes.PackageRequired]: 'This citation could not be resolved.',
  [CitationAssetErrorCodes.AssetRequired]: 'This citation could not be resolved.',
  [CitationAssetErrorCodes.InvalidUrn]: 'This citation could not be resolved.',
  [CitationAssetErrorCodes.UnsupportedAsset]: 'This evidence type is not supported.',
  [CitationAssetErrorCodes.NotFound]: 'This evidence is not available.',
  [CitationAssetErrorCodes.Retired]: 'This evidence has been retired and is no longer available.',
  [CitationAssetErrorCodes.PackageMismatch]: 'This evidence is not available for the active legislation package.',
  [CitationAssetErrorCodes.AccessDenied]: 'You do not have access to this evidence.',
  [CitationAssetErrorCodes.LicenceBlocked]: 'This evidence is restricted by licence and cannot be displayed here.',
  [CitationAssetErrorCodes.UnsupportedMedia]: 'Metadata is available, but this evidence type has no displayable content.',
  [CitationAssetErrorCodes.TamperDetected]: 'This evidence failed an integrity check and has been blocked.',
  unknown_error: 'This evidence could not be loaded.',
};

async function parseErrorCode(response: Response): Promise<CitationAssetErrorCode> {
  try {
    const body = await response.json();
    const code = body?.extensions?.code ?? body?.code;
    if (typeof code === 'string' && code in PRIVACY_SAFE_MESSAGES) {
      return code as CitationAssetErrorCode;
    }
  } catch {
    // Non-JSON or unreadable body: fall through to the generic mapping.
  }
  return 'unknown_error';
}

function toErrorResult(code: CitationAssetErrorCode, httpStatus: number): CitationAssetErrorResult {
  return {
    status: 'error',
    code,
    httpStatus,
    message: PRIVACY_SAFE_MESSAGES[code],
  };
}

function buildQuery(key: CitationAssetKey): string {
  const params = new URLSearchParams({
    urn: key.urn,
    package_version: key.packageVersion,
    asset_id: key.assetId,
  });
  return params.toString();
}

/**
 * Fetch typed descriptor metadata for a citation asset. Always safe to call
 * regardless of licence/access policy; metadata (including licence and
 * attribution) is returned even when content is blocked.
 */
export async function getCitationAssetMetadata(
  key: CitationAssetKey,
  token: string | undefined,
  address: string | null | undefined
): Promise<CitationAssetResult> {
  const host = getApiHost(address);
  const response = await fetch(`${host}${CITATION_ASSETS_ENDPOINT}/metadata?${buildQuery(key)}`, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    const code = await parseErrorCode(response);
    return toErrorResult(code, response.status);
  }

  const descriptor = (await response.json()) as CitationAssetDescriptor;
  cache.set(cacheKey(key), { descriptor, etag: descriptor.sha256 });
  return { status: 'ok', descriptor };
}

/**
 * Fetch binary content for a citation asset. Sends `If-None-Match` using
 * the cached SHA-256 ETag when available so an unchanged immutable asset
 * is revalidated with a 304 instead of re-downloading bytes. Access-policy
 * rejections (403), not-found (404), retired (409), unsupported-media (415)
 * and tamper-detected (422) are all returned as deterministic error results
 * rather than thrown, so callers can render a stable fallback state.
 */
export async function getCitationAssetContent(
  key: CitationAssetKey,
  token: string | undefined,
  address: string | null | undefined
): Promise<CitationAssetContentResult> {
  const host = getApiHost(address);
  const existing = cache.get(cacheKey(key));
  const headers = getAuthHeaders(token);
  // Only send If-None-Match when we already hold usable cached bytes: an
  // ETag cached from a metadata-only fetch (no bytes) can still match the
  // content response's ETag, so sending it unconditionally would make the
  // very first content fetch come back as a bodyless 304 with nothing to
  // fall back to, silently caching/rendering zero bytes.
  if (existing?.etag && existing.bytes) {
    (headers as Record<string, string>)['If-None-Match'] = `"${existing.etag}"`;
  }

  const response = await fetch(`${host}${CITATION_ASSETS_ENDPOINT}/content?${buildQuery(key)}`, {
    method: 'GET',
    headers,
  });

  if (response.status === 304 && existing?.bytes) {
    return { status: 'ok', descriptor: existing.descriptor, bytes: existing.bytes, fromCache: true };
  }

  if (!response.ok && response.status !== 304) {
    const code = await parseErrorCode(response);
    return toErrorResult(code, response.status);
  }

  const bytes = await response.arrayBuffer();
  const etagHeader = response.headers.get('ETag')?.replace(/"/g, '');
  const metadataResult = await getCitationAssetMetadata(key, token, address);
  const descriptor = metadataResult.status === 'ok'
    ? metadataResult.descriptor
    : existing?.descriptor;

  if (!descriptor) {
    return toErrorResult('unknown_error', response.status);
  }

  cache.set(cacheKey(key), { descriptor, etag: etagHeader ?? descriptor.sha256, bytes });
  return { status: 'ok', descriptor, bytes, fromCache: false };
}
